import type { ComputedRef, Ref } from 'vue';
import type { TIconButtonProps } from './TIconButton.types';
import { css } from '@emotion/css';
import { darken, lighten } from 'polished';
import { computed } from 'vue';
import { useTheme } from '../../composables/theme';
import { toCamelCase } from '../../utils';

export const useStyles = (color:
  | Ref<NonNullable<TIconButtonProps['color']>>
  | ComputedRef<NonNullable<TIconButtonProps['color']>>) => {
  const theme = useTheme();
  const variant = computed(() => toCamelCase(color.value));
  const hover = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.toku
    : darken(0.15, theme.value.colors[variant.value])));
  const main = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.main
    : theme.value.colors[variant.value]));
  const light1 = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.light1
    : lighten(0.15, theme.value.colors[variant.value])));
  const light2 = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.light2
    : lighten(0.2, theme.value.colors[variant.value])));
  const light3 = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.light3
    : lighten(0.25, theme.value.colors[variant.value])));
  const baseClass = computed(() => css({
    '&, *': {
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    '&--primary': {
      borderRadius: theme.value.button.borderRadius,
      backgroundColor: main.value,
      boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${main.value}`,
      color: '#FFFFFF',
      '&:hover:not(&:disabled)': {
        backgroundColor: hover.value,
      },
      '&:focus': {
        border: `1px solid ${light1.value}`,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        border: `1px solid ${theme.value.colors.gray.light2}`,
        backgroundColor: theme.value.colors.gray.light2,
        color: theme.value.colors.gray.medium2,
      },
    },
    '&--secondary': {
      borderRadius: theme.value.button.borderRadius,
      backgroundColor: '#FFFFFF',
      border: `1px solid ${main.value}`,
      boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.1)',
      color: main.value,
      '&:hover:not(&:disabled)': {
        backgroundColor: light3.value,
      },
      '&:focus': {
        border: `1px solid ${light1.value}`,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        border: `1px solid ${theme.value.colors.gray.light2}`,
        backgroundColor: theme.value.colors.gray.light2,
        color: theme.value.colors.gray.medium2,
      },
    },
    '&--tertiary': {
      background: 'none',
      border: 'none',
      margin: 0,
      padding: 0,
      color: main.value,
      '&:hover:not(&:disabled)': {
        color: light2.value,
      },
      '&:focus': {
        color: light1.value,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        color: theme.value.colors.gray.medium1,
      },
    },
    '&--large': {
      height: '3rem',
      width: '3rem',
      lineHeight: '1.5rem',
      fontSize: '1.125rem',
    },
    '&--medium': {
      height: '2.5rem',
      width: '2.5rem',
      lineHeight: '1.375rem',
      fontSize: '1rem',
    },
    '&--small': {
      height: '2rem',
      width: '2rem',
      lineHeight: '1.25rem',
      fontSize: '0.875rem',
    },
    '&--rounded': {
      borderRadius: '50%',
    },
  }));

  const iconClass = computed(() => css({
    margin: 0,
    padding: 0,
    '&--large': {
      height: '1.25rem',
    },
    '&--medium': {
      height: '1.25rem',
    },
    '&--small': {
      height: '1rem',
    },
  }));

  return { baseClass, iconClass };
};
